/* eslint-disable @typescript-eslint/no-explicit-any */
import {Exact} from '@iiiristram/ts-type-utils'
import {logEvent} from '@yandex-delivery/modules/rum'
import {delay} from '@yandex-delivery/utils'

import {fetchNetworkInterface as networkInterface} from './fetch-network-interface'
import {ResponseHeaders, TypedRequestConfig} from './types'
import {FetchError, getYaTraceId, ServerError} from './ServerError'

const POOR_LCP = 4000 // Ref: https://web.dev/articles/lcp#what_is_a_good_lcp_score

export type Fetch<TRequestBody = unknown, TResponseBody = unknown, TTransformedBody = unknown> = (
  config: TypedRequestConfig<TRequestBody, TResponseBody, TTransformedBody>,
) => Promise<Exact<TTransformedBody, any> extends true ? TResponseBody : TTransformedBody>

/**
 * @deprecated use fetchWithHeaders instead, will be removed in CARGODEV-57905
 */
export function fetch<TRequestBody = unknown, TResponseBody = unknown, TTransformedBody = unknown>(
  config: TypedRequestConfig<TRequestBody, TResponseBody, TTransformedBody>,
) {
  const {url, method = 'GET', body, headers, options, transform} = config
  const start = Date.now()
  const {execute} = networkInterface(url, method, {
    body,
    headers,
    credentials: options?.credentials,
  })

  return new Promise<Exact<TTransformedBody, any> extends true ? TResponseBody : TTransformedBody>(
    (resolve, reject) => {
      execute((error, status, responseBody, responseText, responseHeaders) => {
        const traceId = getYaTraceId(responseHeaders)

        if (traceId && Date.now() - start > POOR_LCP) {
          logEvent('slow_api', traceId, {url})
        }

        if (error || status >= 400) {
          logEvent('fetch_error', traceId, {url, status})
          reject(new ServerError({error, url, status, body: responseBody, headers: responseHeaders || {}}))
          return
        }

        resolve(responseBody ? (transform ? transform(responseBody) : responseBody) : responseText)
      })
    },
  )
}

export function fetchWithHeaders<TRequestBody = unknown, TResponseBody = unknown, TTransformedBody = unknown>(
  config: TypedRequestConfig<TRequestBody, TResponseBody, TTransformedBody>,
) {
  const {url, method = 'GET', body, headers, options, transform, signal} = config
  const {execute, abort} = networkInterface(url, method, {
    body,
    headers,
    credentials: options?.credentials,
  })

  if (signal) {
    signal.addEventListener('abort', abort)
  }

  return new Promise<{
    body: Exact<TTransformedBody, any> extends true ? TResponseBody : TTransformedBody
    headers: ResponseHeaders
  }>((resolve, reject) => {
    execute((error, status, responseBody, responseText, responseHeaders) => {
      if (error || status >= 400) {
        reject(new ServerError({error, url, status, body: responseBody, headers: responseHeaders || {}}))
        return
      }

      resolve({
        body: responseBody ? (transform ? transform(responseBody) : responseBody) : responseText,
        headers: responseHeaders || {},
      })
    })
  })
}

type FakeFetchProps<TResponse, TError = any> = {
  collapsed?: boolean
  timeoutMs?: number
  response?: TResponse
  error?: TError
}

export const fakeFetch = <TResponse>(props: FakeFetchProps<TResponse> = {}) => {
  const {timeoutMs = 300, collapsed = false, response, error} = props

  // дженерики для совпадения по типам с оригинальным фетчом
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return <A = any, B = any, C = any>(config: TypedRequestConfig<any, TResponse>): Promise<TResponse | undefined> => {
    if (process.env.NODE_ENV === 'development') {
      console[collapsed ? 'groupCollapsed' : 'group']('%c request', 'color: grey', config.url)
      config.body && console.log('%c body    ', 'color: #03A9F4', config.body)
      if (error) {
        console.log('%c error', 'color: #FF0000', error)
      } else if (response) {
        console.log('%c response', 'color: #4CAF50', response)
      }
      console.groupEnd()
    }

    return delay(timeoutMs).then(() => {
      if (error) {
        throw new ServerError({
          headers: {},
          error: new Error('Fake fetch error') as FetchError,
          body: error,
          status: 999,
        })
      }

      return response
    })
  }
}
